export type DnisSdkConfig = {
  propertyKey?: string;
  apiBase?: string;
};

type StoredResolution = {
  phoneNumber: string;
  landingPage: string;
};

type ResolverResponse = {
  phoneNumber: string | null;
  usedFallback: boolean;
};

type OriginalPhoneElement = {
  textContent: string | null;
  href: string | null;
};

const SDK_VERSION = "0.1.0";
const RESOLVE_TIMEOUT_MS = 8_000;
const STORAGE_PREFIX = "hpos:dnis:";
const PHONE_SELECTOR = "[data-hpos-dnis-phone]";
const LINK_SELECTOR = "a[data-hpos-dnis-link], a[data-hpos-dnis-phone]";
const PUBLIC_KEY_PATTERN = /^dnis_[A-Za-z0-9_-]{32}$/;
const E164_PATTERN = /^\+[1-9]\d{7,14}$/;

const formatPhone = (e164: string): string => {
  const digits = e164.replace(/\D/g, "");
  if (digits.length === 11 && digits.startsWith("1")) {
    return `(${digits.slice(1, 4)}) ${digits.slice(4, 7)}-${digits.slice(7)}`;
  }
  return e164;
};

const normalizeApiBase = (value: string): string => value.replace(/\/+$/, "");

export class DnisSdk {
  private configured: DnisSdkConfig = {};
  private activePropertyKey: string | null = null;
  private activeLandingPage: string | null = null;
  private started = false;
  private scheduled = false;
  private scheduledTimer: number | null = null;
  private observer: MutationObserver | null = null;
  private originalPushState: History["pushState"] | null = null;
  private originalReplaceState: History["replaceState"] | null = null;
  private readonly memoryStorage = new Map<string, StoredResolution>();
  private readonly inFlightResolutions = new Map<string, Promise<StoredResolution | null>>();
  private readonly originalPhoneElements = new Map<HTMLElement, OriginalPhoneElement>();
  private readonly windowRef: Window;
  private readonly documentRef: Document;
  private readonly scriptElement: HTMLScriptElement | null;

  constructor(windowRef: Window, documentRef: Document, scriptElement: HTMLScriptElement | null) {
    this.windowRef = windowRef;
    this.documentRef = documentRef;
    this.scriptElement = scriptElement;
  }

  start = (): void => {
    if (this.started) {
      return;
    }
    this.started = true;
    this.patchHistory();
    this.windowRef.addEventListener("popstate", this.scheduleSync);
    this.observer = new MutationObserver(this.scheduleSync);
    this.observer.observe(this.documentRef.documentElement, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ["data-hpos-dnis-property-key"]
    });
    this.scheduleSync();
  };

  configure = (config: DnisSdkConfig): void => {
    this.configured = { ...this.configured, ...config };
    if (this.resolvePropertyKey() !== this.activePropertyKey) {
      this.clearActiveProperty();
    }
    this.scheduleSync();
  };

  stop = (): void => {
    this.observer?.disconnect();
    this.observer = null;
    this.windowRef.removeEventListener("popstate", this.scheduleSync);
    if (this.scheduledTimer !== null) {
      this.windowRef.clearTimeout(this.scheduledTimer);
      this.scheduledTimer = null;
      this.scheduled = false;
    }
    if (this.originalPushState) {
      this.windowRef.history.pushState = this.originalPushState;
      this.originalPushState = null;
    }
    if (this.originalReplaceState) {
      this.windowRef.history.replaceState = this.originalReplaceState;
      this.originalReplaceState = null;
    }
    this.clearActiveProperty();
    this.started = false;
  };

  sync = async (): Promise<void> => {
    const propertyKey = this.resolvePropertyKey();
    if (!propertyKey) {
      this.clearActiveProperty();
      return;
    }
    const landingPage = this.windowRef.location.href;
    const propertyChanged = propertyKey !== this.activePropertyKey;
    const contextChanged = propertyChanged || landingPage !== this.activeLandingPage;
    if (propertyChanged) {
      this.restoreOriginalPhones();
    }
    this.activePropertyKey = propertyKey;
    this.activeLandingPage = landingPage;
    const stored = this.readStoredResolution(propertyKey);
    if (stored?.landingPage === landingPage) {
      this.applyPhone(stored.phoneNumber);
      return;
    }
    if (!contextChanged && this.documentRef.querySelector(`${PHONE_SELECTOR}[data-hpos-dnis-resolved="true"]`)) {
      return;
    }

    const resolutionKey = `${propertyKey}:${landingPage}`;
    let resolution = this.inFlightResolutions.get(resolutionKey);
    if (!resolution) {
      resolution = this.resolve(propertyKey, landingPage, stored);
      this.inFlightResolutions.set(resolutionKey, resolution);
      const clearResolution = () => {
        if (this.inFlightResolutions.get(resolutionKey) === resolution) {
          this.inFlightResolutions.delete(resolutionKey);
        }
      };
      void resolution.then(clearResolution, clearResolution);
    }
    const result = await resolution;
    if (result && this.activePropertyKey === propertyKey && this.resolvePropertyKey() === propertyKey && this.windowRef.location.href === landingPage) {
      this.applyPhone(result.phoneNumber);
    }
  };

  private clearActiveProperty = (): void => {
    this.restoreOriginalPhones();
    this.inFlightResolutions.clear();
    this.activePropertyKey = null;
    this.activeLandingPage = null;
  };

  private resolve = async (propertyKey: string, landingPage: string, stored: StoredResolution | null): Promise<StoredResolution | null> => {
    const controller = new AbortController();
    const timeout = this.windowRef.setTimeout(() => controller.abort(), RESOLVE_TIMEOUT_MS);
    try {
      const response = await this.windowRef.fetch(`${this.resolveApiBase()}/properties/${encodeURIComponent(propertyKey)}/resolve`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        signal: controller.signal,
        body: JSON.stringify({
          landingPage,
          sdkVersion: SDK_VERSION
        })
      });
      if (!response.ok) {
        return null;
      }
      const result = (await response.json()) as ResolverResponse;
      if (!result.phoneNumber || !E164_PATTERN.test(result.phoneNumber)) {
        return null;
      }
      const nextResolution = {
        phoneNumber: result.usedFallback && stored ? stored.phoneNumber : result.phoneNumber,
        landingPage
      };
      this.writeStoredResolution(propertyKey, nextResolution);
      return nextResolution;
    } catch {
      return null;
    } finally {
      this.windowRef.clearTimeout(timeout);
    }
  };

  private scheduleSync = (): void => {
    if (this.scheduled) {
      return;
    }
    this.scheduled = true;
    this.scheduledTimer = this.windowRef.setTimeout(() => {
      this.scheduledTimer = null;
      this.scheduled = false;
      void this.sync();
    }, 0);
  };

  private resolvePropertyKey = (): string | null => {
    const rootKey = this.documentRef.querySelector<HTMLElement>("[data-hpos-dnis-property-key]")?.dataset.hposDnisPropertyKey;
    const key = this.configured.propertyKey ?? rootKey ?? this.scriptElement?.dataset.propertyKey;
    return key && PUBLIC_KEY_PATTERN.test(key) ? key : null;
  };

  private resolveApiBase = (): string => {
    const explicit = this.configured.apiBase ?? this.scriptElement?.dataset.apiBase;
    if (explicit) {
      return normalizeApiBase(explicit);
    }
    const scriptOrigin = this.scriptElement?.src ? new URL(this.scriptElement.src, this.documentRef.baseURI).origin : this.windowRef.location.origin;
    return `${scriptOrigin}/crm-api/public/v1/dnis`;
  };

  private applyPhone = (phoneNumber: string): void => {
    this.purgeDetachedPhoneElements();
    const formatted = formatPhone(phoneNumber);
    this.documentRef.querySelectorAll<HTMLElement>(PHONE_SELECTOR).forEach(element => {
      this.rememberOriginalPhone(element);
      if (element.textContent !== formatted) {
        element.textContent = formatted;
      }
      element.dataset.hposDnisResolved = "true";
    });
    this.documentRef.querySelectorAll<HTMLAnchorElement>(LINK_SELECTOR).forEach(element => {
      this.rememberOriginalPhone(element);
      const href = `tel:${phoneNumber}`;
      if (element.getAttribute("href") !== href) {
        element.setAttribute("href", href);
      }
    });
  };

  private rememberOriginalPhone = (element: HTMLElement): void => {
    if (!this.originalPhoneElements.has(element)) {
      this.originalPhoneElements.set(element, { textContent: element.textContent, href: element.getAttribute("href") });
    }
  };

  private purgeDetachedPhoneElements = (): void => {
    for (const element of this.originalPhoneElements.keys()) {
      if (!element.isConnected) {
        this.originalPhoneElements.delete(element);
      }
    }
  };

  private restoreOriginalPhones = (): void => {
    for (const [element, original] of this.originalPhoneElements) {
      if (!element.isConnected) {
        this.originalPhoneElements.delete(element);
        continue;
      }
      element.textContent = original.textContent;
      if (original.href === null) {
        element.removeAttribute("href");
      } else {
        element.setAttribute("href", original.href);
      }
      delete element.dataset.hposDnisResolved;
      this.originalPhoneElements.delete(element);
    }
  };

  private readStoredResolution = (propertyKey: string): StoredResolution | null => {
    const memoryValue = this.memoryStorage.get(propertyKey);
    try {
      const raw = this.windowRef.sessionStorage.getItem(`${STORAGE_PREFIX}${propertyKey}`);
      if (!raw) {
        return memoryValue ?? null;
      }
      const parsed = JSON.parse(raw) as Partial<StoredResolution>;
      if (typeof parsed.phoneNumber !== "string" || !E164_PATTERN.test(parsed.phoneNumber) || typeof parsed.landingPage !== "string") {
        return memoryValue ?? null;
      }
      return { phoneNumber: parsed.phoneNumber, landingPage: parsed.landingPage };
    } catch {
      return memoryValue ?? null;
    }
  };

  private writeStoredResolution = (propertyKey: string, value: StoredResolution): void => {
    this.memoryStorage.set(propertyKey, value);
    try {
      this.windowRef.sessionStorage.setItem(`${STORAGE_PREFIX}${propertyKey}`, JSON.stringify(value));
    } catch {
      // The in-memory copy keeps the SDK functional when browser storage is blocked.
    }
  };

  private patchHistory = (): void => {
    this.originalPushState = this.windowRef.history.pushState;
    this.originalReplaceState = this.windowRef.history.replaceState;
    this.windowRef.history.pushState = (data: unknown, unused: string, url?: string | URL | null) => {
      this.originalPushState?.call(this.windowRef.history, data, unused, url);
      this.scheduleSync();
    };
    this.windowRef.history.replaceState = (data: unknown, unused: string, url?: string | URL | null) => {
      this.originalReplaceState?.call(this.windowRef.history, data, unused, url);
      this.scheduleSync();
    };
  };
}
